export const ENTITY_TYPES = {
  legal: "LEGALENTITY",
  merchant: "MERCHANTEMPLOYEE",
};

export const ENTITY_ROLES = {
  supervisor: "Supervisor",
};

export const BSL_STATUS = {
  apply: 0,
  pending: 1,
  approved: 2,
  rejected: 3,
};

export const SIDEBAR_SESSION_EVENT = "new-sidebar-session-updated";

export function readStorageValue(key, fallback = "") {
  return sessionStorage.getItem(key) || fallback;
}

export function readStorageNumber(key, fallback = 0) {
  const value = Number(sessionStorage.getItem(key));
  return Number.isFinite(value) ? value : fallback;
}

export function readStorageJson(key, fallback = []) {
  try {
    const rawValue = sessionStorage.getItem(key);
    if (!rawValue || rawValue === "null") return fallback;
    const parsedValue = JSON.parse(rawValue);
    return parsedValue ?? fallback;
  } catch {
    return fallback;
  }
}

export function uniqueBy(items, getKey) {
  const seen = new Set();
  return (Array.isArray(items) ? items : []).filter((item) => {
    const key = getKey(item);
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
}

export function featureSlugFromName(name) {
  return String(name || "")
    .toLowerCase()
    .replace(/ /g, "");
}

export function notifySidebarSessionUpdated() {
  if (typeof window === "undefined") return;
  window.dispatchEvent(
    new CustomEvent(SIDEBAR_SESSION_EVENT, {
      detail: { timestamp: Date.now() },
    }),
  );
}

export function normalizeSidebarKey(value) {
  return String(value || "")
    .toLowerCase()
    .replace(/[^a-z0-9]/g, "");
}

export function humanizeSidebarKey(value, fallback = "Overview") {
  const normalized = String(value || "")
    .replace(/^\/+/, "")
    .split("/")[0]
    .replace(/[-_]+/g, " ")
    .trim();

  if (!normalized) return fallback;

  return normalized.replace(/\b\w/g, (character) => character.toUpperCase());
}

export function buildSidebarPages(categories = [], tabs = []) {
  const pages = [];

  (Array.isArray(categories) ? categories : []).forEach((category) => {
    const sectionTitle = category?.Description || "Dashboard";

    (Array.isArray(category?.Sections) ? category.Sections : []).forEach(
      (section) => {
        (Array.isArray(section?.Features) ? section.Features : []).forEach(
          (feature) => {
            if (!feature?.IsPage || !feature?.Slug) return;

            const slug = String(feature.Slug);
            const pageTitle = feature?.Description || slug;
            const normalizedSlug = normalizeSidebarKey(slug);

            pages.push({
              slug,
              routeKey: normalizedSlug,
              page: pageTitle,
              section: sectionTitle,
              aliases: uniqueBy(
                [
                  normalizedSlug,
                  normalizeSidebarKey(pageTitle),
                  normalizeSidebarKey(sectionTitle),
                ].filter(Boolean),
                (item) => item,
              ),
            });
          },
        );
      },
    );
  });

  (Array.isArray(tabs) ? tabs : []).forEach((tab) => {
    if (!tab?.Slug) return;

    const slug = String(tab.Slug);
    const pageTitle = tab?.InnerDescription || tab?.OuterDescription || slug;
    const sectionTitle = tab?.OuterDescription || "Transactions";
    const normalizedSlug = normalizeSidebarKey(slug);

    pages.push({
      slug,
      routeKey: normalizedSlug,
      page: pageTitle,
      section: sectionTitle,
      aliases: uniqueBy(
        [
          normalizedSlug,
          normalizeSidebarKey(pageTitle),
          normalizeSidebarKey(sectionTitle),
        ].filter(Boolean),
        (item) => item,
      ),
    });
  });

  return uniqueBy(
    pages.filter((page) => page?.routeKey),
    (page) => `${page.routeKey}|${page.page}|${page.section}`,
  );
}

export function resolveSidebarBreadcrumb({
  pathname,
  sidebarPages = [],
  activeKey = "",
  stateName = "",
}) {
  const routeKey = normalizeSidebarKey(
    String(pathname || "").replace(/^\/+/, "").split("/")[0],
  );
  const activeRouteKey = normalizeSidebarKey(activeKey);
  const stateRouteKey = normalizeSidebarKey(stateName);

  const lookupKeys = [routeKey, activeRouteKey, stateRouteKey].filter(Boolean);
  const pages = Array.isArray(sidebarPages) ? sidebarPages : [];

  const exactMatch = pages.find((page) =>
    lookupKeys.some(
      (key) => key === page.routeKey || page.aliases?.includes(key),
    ),
  );

  if (exactMatch) {
    return {
      section: exactMatch.section,
      page: stateName || exactMatch.page,
    };
  }

  const fuzzyMatch = pages.find((page) =>
    lookupKeys.some(
      (key) =>
        key.includes(page.routeKey) ||
        page.routeKey.includes(key) ||
        page.aliases?.some((alias) => key.includes(alias) || alias.includes(key)),
    ),
  );

  if (fuzzyMatch) {
    return {
      section: fuzzyMatch.section,
      page: stateName || fuzzyMatch.page,
    };
  }

  const fallbackPage = stateName || humanizeSidebarKey(routeKey);
  return {
    section: fallbackPage,
    page: fallbackPage,
  };
}
